<template>
  <video
    :id="videoId"
    ref="video"
    class="video-js mx-auto vjs-big-play-centered vjs-show-big-play-button-on-pause"
    :class="divClass"
    :poster="poster"
    playsinline
  />
</template>

<script>
import $ from 'jquery'
import each from 'lodash/each'
import findIndex from 'lodash/findIndex'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import random from 'lodash/random'
import sortBy from 'lodash/sortBy'
import videojs from 'video.js'
import contextmenuUI from 'videojs-contextmenu-ui'
import { computed, markRaw, ref } from 'vue'
import STORAGE_KEYS from '@/common/storage-keys.js'
import constants from '@/generated/constants.js'
import { useSafeLocalStorage } from '@/hooks/use-safe-local-storage'
import { download_file, is_touchable_device } from '../../utils'
import BackButton from './video.js/back-button'
import ControlBarPlugin from './video.js/control-bar-plugin'
import { CopyLinkButton } from './video.js/copy-link-button'
import DubbUiPlugin from './video.js/dubb-ui-plugin'
import ForwardButton from './video.js/forward-button'
import LogoPlugin from './video.js/logo-plugin'
import QualityMenuPlugin from './video.js/quality-menu-plugin'
import RatePlugin from './video.js/rate-plugin'
import { ResumePlaybackPlugin } from './video.js/resume-playback-plugin'
import SettingsMenuPlugin from './video.js/settings-menu-plugin'
import socialSharePlugin from './video.js/social-share-plugin'
import turnOnSoundButtonPlugin from './video.js/turn-on-sound-plugin'
import { vjs_El } from './video.js/utils'
import ZoomPlugin from './video.js/zoom-plugin'
import 'videojs-youtube'

export default {
  name: 'VideoJs',
  props: {
    setup: {},
    downloadOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          videoUrl: null,
          transcriptUrl: null,
        }
      },
    },
    logoOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          img: null,
          position: 'TL',
        }
      },
    },
    elId: {},
    poster: {},
    source: {},
    defaultRes: {
      type: String,
      default: '360',
    },
    turnOnSoundButton: {
      default: false,
    },
    posterBgColor: {
      type: String,
      default: '#ededed',
    },
    resolutions: {
      default: () => {
        return {}
      },
    },
    divClass: {},
    subtitlePath: {},
    contextMenu: {
      default: () => {
        return []
      },
    },
    referUrl: {},
    brandingUrl: {},
    shareOptions: {
      default: () => {
        return {}
      },
    },
    resumePlaybackId: {},
    shouldHidePlayerControlBar: {
      type: Boolean,
      default: false,
    },
    shouldHideSeekButtons: {
      type: Boolean,
      default: false,
    },
    shouldShowCopyLinkButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['ready'],
  setup() {
    // useSafeLocalStorage degrades to a non-persistent ref inside embed
    // iframes where accessing window.localStorage throws a SecurityError.
    const captionEnabled = useSafeLocalStorage(STORAGE_KEYS.VIDEO_PLAYER.CAPTION_ENABLED, true)
    const captionTrackMode = computed(() => captionEnabled.value ? 'showing' : 'disabled')
    const isReloadingSubtitle = ref(false)

    return {
      captionEnabled,
      captionTrackMode,
      isReloadingSubtitle,
    }
  },
  data() {
    return {
      player: null,
      autoplaying: true,
      videoId: this.elId,
    }
  },
  computed: {
    simpleSrcs() {
      if (this.resolutionSrcs.length > 0 || (this.setup.sources && this.setup.sources.length > 0)) {
        return []
      }
      if (isObject(this.source)) {
        const ret = []
        each(this.source, (src, type) => {
          ret.push({ src, type })
        })
        return ret
      }

      if (!this.source) {
        return []
      }

      let type
      if (this.source.endsWith('.m3u8')) {
        type = 'application/x-mpegURL'
      }
      else {
        type = 'video/mp4'
      }
      return [{ type, src: this.source }]
    },
    resolutionSrcs() {
      let srcs = sortBy(map(this.resolutions, (item, key) => {
        return {
          src: item,
          type: item.endsWith('.m3u8') ? 'application/x-mpegURL' : 'video/mp4',
          label: constants.GLOBAL.VIDEO_RESOLUTION_LABEL_MAP[key],
          res: constants.GLOBAL.VIDEO_RESOLUTION_RES_MAP[key],
          default: false,
        }
      }), (item) => {
        return Number.parseInt(item.res)
      })
      if (srcs.length > 0) {
        let index = findIndex(srcs, { res: this.defaultRes })
        if (index < 0) {
          index = 0
        }
        srcs[index].default = true
      }
      srcs = sortBy(srcs, item => !item.default)

      return srcs
    },
    sources() {
      if (this.setup.sources && this.setup.sources.length > 0) {
        return this.setup.sources
      }
      if (this.simpleSrcs.length > 0) {
        return this.simpleSrcs
      }
      return this.resolutionSrcs
    },
  },

  watch: {
    'setup.poster': function (newVal) {
      this.player.poster(newVal)
    },
    resumePlaybackId(newVal) {
      this.player.ResumePlaybackPlugin().dispose()
      this.player.ResumePlaybackPlugin({ uuid: newVal, playbackOffset: 5 })
    },
  },
  created() {
    if (!this.videoId) {
      this.videoId = `videoid-${random(100000, 999999)}`
    }
  },
  mounted() {
    const plugins = {
      contextmenuUI,
      ControlBarPlugin,
      DubbUiPlugin,
      LogoPlugin,
      QualityMenuPlugin,
      RatePlugin,
      ResumePlaybackPlugin,
      SettingsMenuPlugin,
      socialSharePlugin,
      turnOnSoundButtonPlugin,
      ZoomPlugin,
    }

    Object.keys(plugins).forEach((name) => {
      if (!videojs.getPlugin(name)) {
        videojs.registerPlugin(name, plugins[name])
      }
    })

    videojs.registerComponent('CopyLinkButton', CopyLinkButton)
    videojs.registerComponent('BackButton', BackButton)
    videojs.registerComponent('ForwardButton', ForwardButton)
    const setup = Object.assign({}, {
      language: 'en',
      languages: {
        en: {
          LIVE: 'LOADING',
        },
      },
      controlBar: {},
    }, this.setup)
    if (this.poster) {
      setup.poster = this.poster
    }
    if (setup.controlBar !== false && !setup.controlBar.remainingTimeDisplay) {
      setup.controlBar.remainingTimeDisplay = false
    }

    const player = videojs(this.$refs.video, setup, () => {
      let aboutLink = 'https://dubb.com/'
      if (this.referUrl) {
        aboutLink = this.referUrl
      }
      this.player.contextmenuUI({
        content: [{
          href: aboutLink,
          label: 'About Dubb',
        }].concat(this.contextMenu),
      })

      if (this.turnOnSoundButton) {
        this.player.turnOnSoundButtonPlugin()
      }

      if (setup.controls !== false) {
        this.player.ControlBarPlugin({
          pipButton: false,
        })
        this.player.QualityMenuPlugin()
        this.player.SettingsMenuPlugin({})
        this.player.RatePlugin({})
        this.player.ZoomPlugin({})
        this.player.DubbUiPlugin({})

        if (this.downloadOptions.transcriptUrl) {
          this.addSettingButton('Download Transcript', (downloadTranscriptBtnEl) => {
            downloadTranscriptBtnEl.innerHTML = 'Downloading...'
            download_file(this.downloadOptions.transcriptUrl, () => {
              downloadTranscriptBtnEl.innerHTML = 'Download Transcript'
            })
          })
        }

        if (this.downloadOptions.videoUrl) {
          this.addSettingButton('Download Video', this.downloadOptions.videoUrl)
        }

        this.addSettingButton('Report Issue', '/contact')
        this.addSettingButton('About Dubb', 'https://dubb.com/')
      }

      if (this.logoOptions.img) {
        this.player.LogoPlugin({
          img: this.logoOptions.img,
          position: this.logoOptions.position,
        })
      }

      if (this.shareOptions.url) {
        this.player.socialSharePlugin(this.shareOptions)
      }
      if (this.resumePlaybackId) {
        this.player.ResumePlaybackPlugin({ uuid: this.resumePlaybackId, playbackOffset: 5 })
      }

      this.reloadSubtitle()
    })

    this.player = markRaw(player)

    this.player.src(this.sources)

    this.player.on('ready', () => {
      const controlBar = this.player.controlBar

      if (this.player.currentWidth() > 540 && !this.shouldHideSeekButtons) {
        if (controlBar) {
          if (!controlBar.getChild('ForwardButton')) {
            controlBar.addChild('ForwardButton', {}, 2)
          }

          if (!controlBar.getChild('BackButton')) {
            controlBar.addChild('BackButton', {}, 2)
          }
        }
      }

      if (this.shouldShowCopyLinkButton) {
        if (controlBar && !controlBar.getChild('CopyLinkButton')) {
          controlBar.addChild('CopyLinkButton', {}, 15)
        }
      }

      if (this.shouldHidePlayerControlBar) {
        this.hidePlayerControlBar()
      }

      // Listen for caption track changes and save to localStorage
      this.setupCaptionStateListener()

      // Sources are applied imperatively via player.src() after init, so
      // DubbUiPlugin's init-time `sourcesready` never fires for them. Re-fire
      // it here so QualityMenuPlugin can build the resolution menu from the
      // res/label/default carried on each source.
      if (this.sources.length > 0) {
        this.player.trigger('sourcesready', this.sources)
      }
    })

    const Button = videojs.getComponent('Button')
    const dubbBrandingButton = new Button(player, {
      controlText: 'Video powered by Dubb',
      className: 'vjs-dubb-branding',
      clickHandler: () => {
        window.open(this.brandingUrl)
      },
    })
    dubbBrandingButton.hide()
    player.addChild(dubbBrandingButton)

    this.player.on('play', () => {
      $(this.$refs.video).closest('.video-js').removeClass('vjs-show-big-play-button-on-pause')
      if (this.autoplaying) {
        this.autoplaying = false
        return
      }

      dubbBrandingButton.hide()
      this.player.options_.inactivityTimeout = 2000
    })
    this.player.on('ended', () => {
      if (!is_touchable_device()) {
        if (this.player.autoplay()) {
          this.player.autoplay(false)
        }
      }
      if (this.brandingUrl) {
        dubbBrandingButton.show()
      }
    })
    $(`#${this.videoId}`).find('.vjs-poster').css({ 'background-color': this.posterBgColor })
    this.$emit('ready', this.player)
  },
  methods: {
    reload() {
      this.player.poster(this.setup.poster ? this.setup.poster : this.poster)

      this.reloadSubtitle()
      this.player.src(this.sources)
      this.player.trigger('reload', this.sources)
    },
    reloadSubtitle() {
      const tracks = this.player.remoteTextTracks()

      for (let i = 0; i < tracks.length; i++) {
        this.player.removeRemoteTextTrack(tracks[i])
      }

      if (this.subtitlePath) {
        this.player.addRemoteTextTrack({
          src: this.subtitlePath,
          kind: 'subtitles',
          label: 'English',
          srclang: 'en',
          default: this.captionEnabled,
          mode: this.captionTrackMode,
        }, true)
      }
    },

    addSettingButton(name, cb) {
      const btnEl = vjs_El('li', 'vjs-settings-item', name)
      btnEl.ariaDisabled = 'false'
      btnEl.ariaLabel = name
      btnEl.role = 'menuitem'

      btnEl.onclick = btnEl.ontouchend = (e) => {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (isString(cb)) {
          window.open(cb)
        }
        else {
          cb(btnEl)
        }
      }

      this.player.SettingsMenuPlugin().addMenu(name, btnEl, null)
    },

    hidePlayerControlBar() {
      for (const child of this.player.controlBar.children()) {
        if (child.name() !== 'PlayToggle') {
          child.hide()
        }
      }
    },

    handleCaptionTrackChange() {
      const tracks = this.player.remoteTextTracks()
      let isEnabled = false

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        if ((track.kind === 'subtitles' || track.kind === 'captions') && track.mode === 'showing') {
          isEnabled = true
          break
        }
      }

      // Update the reactive localStorage property
      this.captionEnabled = isEnabled
    },

    setupCaptionStateListener() {
      // Listen directly to remoteTextTracks change event
      this.player.remoteTextTracks().addEventListener('change', this.handleCaptionTrackChange)
    },
  },
}
</script>

<style lang="scss" scoped>
  .video-js {
    :deep() {
      // Disable tooltips on captions popup menu items, it looks weird and unnecessary
      .vjs-subs-caps-button .vjs-menu .vjs-control-text {
        display: none !important;
      }

      .vjs-fontawesome {
        .vjs-icon-placeholder::before {
          -webkit-font-smoothing: antialiased;
          cursor: pointer;
          font-size: 16px;
          display: inline-block;
          line-height: 32px;
          width: 1em;
          height: 1em;
          background-color: currentColor;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-size: 100% 100%;
          mask-size: 100% 100%;
        }
      }

      .vjs-dubb-branding {
        position: absolute;
        background: rgba(0, 0, 0, 0.3);
        color: white;
        padding: 0.8rem 1rem;
        bottom: 5%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 5px !important;
        cursor: pointer;
        border: none;

        .vjs-control-text {
          font-size: 14px;
          position: static;
          width: auto;
          white-space: nowrap;
          display: block !important;
        }
      }

      .vjs-forward-button,
      .vjs-back-button {
        line-height: 1.2;
        &::after {
          content: '15';
          font-size: 14px;
          position: absolute;
          width: 100%;
          text-align: center;
          left: 0;
          transform: scale(0.4);
        }
      }

      .vjs-forward-button {
        .vjs-icon-placeholder::before {
          content: '';
          -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M417.3 360.2c3.2-5 8.7-8.2 14.7-8.2c12.3 0 20.3 12.8 13.7 23.2C406 438.2 335.9 480 256 480C132.3 480 32 379.7 32 256S132.3 32 256 32c56.1 0 107.4 20.6 146.7 54.7L433.4 56c5.1-5.1 12.1-8 19.3-8C467.8 48 480 60.2 480 75.3L480 176c0 8.8-7.2 16-16 16l-100.7 0c-15.1 0-27.3-12.2-27.3-27.3c0-7.2 2.9-14.2 8-19.3l36-36C346.5 81.1 303.3 64 256 64C150 64 64 150 64 256s86 192 192 192c67.6 0 127.1-35 161.3-87.8zM448 86.6L374.6 160l73.4 0 0-73.4z' /%3E%3C/svg%3E");
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M417.3 360.2c3.2-5 8.7-8.2 14.7-8.2c12.3 0 20.3 12.8 13.7 23.2C406 438.2 335.9 480 256 480C132.3 480 32 379.7 32 256S132.3 32 256 32c56.1 0 107.4 20.6 146.7 54.7L433.4 56c5.1-5.1 12.1-8 19.3-8C467.8 48 480 60.2 480 75.3L480 176c0 8.8-7.2 16-16 16l-100.7 0c-15.1 0-27.3-12.2-27.3-27.3c0-7.2 2.9-14.2 8-19.3l36-36C346.5 81.1 303.3 64 256 64C150 64 64 150 64 256s86 192 192 192c67.6 0 127.1-35 161.3-87.8zM448 86.6L374.6 160l73.4 0 0-73.4z' /%3E%3C/svg%3E");
        }
      }

      .vjs-back-button {
        margin-left: 10px;

        .vjs-icon-placeholder {
          &::before {
            transform: scale(-1, 1);
            content: '';
            -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M417.3 360.2c3.2-5 8.7-8.2 14.7-8.2c12.3 0 20.3 12.8 13.7 23.2C406 438.2 335.9 480 256 480C132.3 480 32 379.7 32 256S132.3 32 256 32c56.1 0 107.4 20.6 146.7 54.7L433.4 56c5.1-5.1 12.1-8 19.3-8C467.8 48 480 60.2 480 75.3L480 176c0 8.8-7.2 16-16 16l-100.7 0c-15.1 0-27.3-12.2-27.3-27.3c0-7.2 2.9-14.2 8-19.3l36-36C346.5 81.1 303.3 64 256 64C150 64 64 150 64 256s86 192 192 192c67.6 0 127.1-35 161.3-87.8zM448 86.6L374.6 160l73.4 0 0-73.4z' /%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M417.3 360.2c3.2-5 8.7-8.2 14.7-8.2c12.3 0 20.3 12.8 13.7 23.2C406 438.2 335.9 480 256 480C132.3 480 32 379.7 32 256S132.3 32 256 32c56.1 0 107.4 20.6 146.7 54.7L433.4 56c5.1-5.1 12.1-8 19.3-8C467.8 48 480 60.2 480 75.3L480 176c0 8.8-7.2 16-16 16l-100.7 0c-15.1 0-27.3-12.2-27.3-27.3c0-7.2 2.9-14.2 8-19.3l36-36C346.5 81.1 303.3 64 256 64C150 64 64 150 64 256s86 192 192 192c67.6 0 127.1-35 161.3-87.8zM448 86.6L374.6 160l73.4 0 0-73.4z' /%3E%3C/svg%3E");
          }
        }
      }

      .vjs-copy-link-button {
        .vjs-icon-placeholder::before {
          content: '';
          -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='currentColor' d='M591.5 256c50-50 50-131 0-181s-131-50-181 0L387.9 97.6c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l22.6-22.6c37.5-37.5 98.3-37.5 135.8 0s37.5 98.3 0 135.8L444.3 357.9c-37.4 37.4-98.1 37.4-135.6 0c-35.6-35.6-37.6-92.6-4.7-130.6l5.3-6.1c5.8-6.7 5.1-16.8-1.6-22.6s-16.8-5.1-22.6 1.6l-5.3 6.1c-43.9 50.7-41.2 126.7 6.2 174.1c49.9 49.9 130.9 49.9 180.8 0L591.5 256zM48.5 256c-50 50-50 131 0 181s131 50 181 0l22.6-22.6c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0l-22.6 22.6c-37.5 37.5-98.3 37.5-135.8 0s-37.5-98.3 0-135.8L195.7 154.1c37.4-37.4 98.1-37.4 135.6 0c35.6 35.6 37.6 92.6 4.7 130.6l-5.3 6.1c-5.8 6.7-5.1 16.8 1.6 22.6s16.8 5.1 22.6-1.6l5.3-6.1c43.9-50.7 41.2-126.7-6.2-174.1C303.9 81.5 223 81.5 173 131.4L48.5 256z' /%3E%3C/svg%3E");
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='currentColor' d='M591.5 256c50-50 50-131 0-181s-131-50-181 0L387.9 97.6c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0l22.6-22.6c37.5-37.5 98.3-37.5 135.8 0s37.5 98.3 0 135.8L444.3 357.9c-37.4 37.4-98.1 37.4-135.6 0c-35.6-35.6-37.6-92.6-4.7-130.6l5.3-6.1c5.8-6.7 5.1-16.8-1.6-22.6s-16.8-5.1-22.6 1.6l-5.3 6.1c-43.9 50.7-41.2 126.7 6.2 174.1c49.9 49.9 130.9 49.9 180.8 0L591.5 256zM48.5 256c-50 50-50 131 0 181s131 50 181 0l22.6-22.6c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0l-22.6 22.6c-37.5 37.5-98.3 37.5-135.8 0s-37.5-98.3 0-135.8L195.7 154.1c37.4-37.4 98.1-37.4 135.6 0c35.6 35.6 37.6 92.6 4.7 130.6l-5.3 6.1c-5.8 6.7-5.1 16.8 1.6 22.6s16.8 5.1 22.6-1.6l5.3-6.1c43.9-50.7 41.2-126.7-6.2-174.1C303.9 81.5 223 81.5 173 131.4L48.5 256z' /%3E%3C/svg%3E");
        }
      }

      .vjs-turn-on-sound {
        position: absolute;
        background: rgba(255,255,255,0.8);
        color: black;
        padding: 0.8rem 1rem;
        inset: 0;
        margin: auto;
        border-radius: 5px;
        border-width: 1px;
        cursor: pointer;
        height: 48px;
        width: 160px;

        .vjs-control-text {
          font-size: 14px;
          &:before {
            content: '';
            margin-right: 5px;
            display: inline-block;
            width: 1em;
            height: 1em;
            background-color: currentColor;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            -webkit-mask-size: 100% 100%;
            mask-size: 100% 100%;
            -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='currentColor' d='M151.6 188L288 66.7l0 378.6L151.6 324c-2.9-2.6-6.7-4-10.6-4l-85 0c-13.3 0-24-10.7-24-24l0-80c0-13.3 10.7-24 24-24l85 0c3.9 0 7.7-1.4 10.6-4zM290.2 32c-7.3 0-14.3 2.7-19.8 7.5L134.9 160 56 160c-30.9 0-56 25.1-56 56l0 80c0 30.9 25.1 56 56 56l78.9 0L270.4 472.5l10.6-12-10.6 12c5.5 4.8 12.5 7.5 19.8 7.5c16.5 0 29.8-13.3 29.8-29.8l0-388.4C320 45.3 306.7 32 290.2 32zM409.6 179.2c-7.1-5.3-17.1-3.9-22.4 3.2s-3.9 17.1 3.2 22.4C406 216.5 416 235.1 416 256s-10 39.5-25.6 51.2c-7.1 5.3-8.5 15.3-3.2 22.4s15.3 8.5 22.4 3.2C432.9 315.3 448 287.4 448 256s-15.1-59.3-38.4-76.8zm42-69.2c-5.6 6.8-4.7 16.9 2.1 22.5C489.4 161.9 512 206.3 512 256s-22.6 94.1-58.2 123.4c-6.8 5.6-7.8 15.7-2.1 22.5s15.7 7.8 22.5 2.1C516.8 368.9 544 315.6 544 256s-27.2-112.9-69.8-148.1c-6.8-5.6-16.9-4.7-22.5 2.1zm86.7-73.6c-6.7-5.7-16.8-4.9-22.6 1.8s-4.9 16.8 1.8 22.6c55.3 47 90.4 117 90.4 195.2s-35.1 148.2-90.4 195.2c-6.7 5.7-7.6 15.8-1.8 22.6s15.8 7.6 22.6 1.8C600.5 422.8 640 344 640 256s-39.5-166.8-101.6-219.6z' /%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='currentColor' d='M151.6 188L288 66.7l0 378.6L151.6 324c-2.9-2.6-6.7-4-10.6-4l-85 0c-13.3 0-24-10.7-24-24l0-80c0-13.3 10.7-24 24-24l85 0c3.9 0 7.7-1.4 10.6-4zM290.2 32c-7.3 0-14.3 2.7-19.8 7.5L134.9 160 56 160c-30.9 0-56 25.1-56 56l0 80c0 30.9 25.1 56 56 56l78.9 0L270.4 472.5l10.6-12-10.6 12c5.5 4.8 12.5 7.5 19.8 7.5c16.5 0 29.8-13.3 29.8-29.8l0-388.4C320 45.3 306.7 32 290.2 32zM409.6 179.2c-7.1-5.3-17.1-3.9-22.4 3.2s-3.9 17.1 3.2 22.4C406 216.5 416 235.1 416 256s-10 39.5-25.6 51.2c-7.1 5.3-8.5 15.3-3.2 22.4s15.3 8.5 22.4 3.2C432.9 315.3 448 287.4 448 256s-15.1-59.3-38.4-76.8zm42-69.2c-5.6 6.8-4.7 16.9 2.1 22.5C489.4 161.9 512 206.3 512 256s-22.6 94.1-58.2 123.4c-6.8 5.6-7.8 15.7-2.1 22.5s15.7 7.8 22.5 2.1C516.8 368.9 544 315.6 544 256s-27.2-112.9-69.8-148.1c-6.8-5.6-16.9-4.7-22.5 2.1zm86.7-73.6c-6.7-5.7-16.8-4.9-22.6 1.8s-4.9 16.8 1.8 22.6c55.3 47 90.4 117 90.4 195.2s-35.1 148.2-90.4 195.2c-6.7 5.7-7.6 15.8-1.8 22.6s15.8 7.6 22.6 1.8C600.5 422.8 640 344 640 256s-39.5-166.8-101.6-219.6z' /%3E%3C/svg%3E");
          }
          position: static;
          width: auto;
          white-space: nowrap;
        }
      }

      .vjs-social-share-buttons {
        position: absolute;
        background: rgba(255,255,255,.8);
        color: white;
        padding: .8rem .7rem .3rem;
        top: 2%;
        right: 1%;
        border-radius: 5px !important;

        .vjs-social-share-button {
          .vjs-icon-placeholder::before {
            -webkit-font-smoothing: antialiased;
            cursor: pointer;
            font-size: 16px;
            display: inline-block;
            line-height: 32px;
            width: 1em;
            height: 1em;
            background-color: currentColor;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            -webkit-mask-size: 100% 100%;
            mask-size: 100% 100%;
          }
        }
        .vjs-social-share-close-button {
          text-align: center;
          line-height: 14px;
          .vjs-control-text {
            height: auto;
            width: auto;
            font-size: 14px;
            position: unset;
          }
        }
        .vjs-social-share-facebook {
          color: #3b5998;
          .vjs-icon-placeholder::before {
            content: '';
            -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256h0z' /%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256h0z' /%3E%3C/svg%3E");
          }
        }
        .vjs-social-share-twitter {
          color: #1da1f2;
          .vjs-icon-placeholder::before {
            content: '';
            -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z' /%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='currentColor' d='M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z' /%3E%3C/svg%3E");
          }
        }
        .vjs-social-share-linkedin {
          color: #0077b5;
          .vjs-icon-placeholder::before {
            content: '';
            -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'%3E%3Cpath fill='currentColor' d='M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z' /%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'%3E%3Cpath fill='currentColor' d='M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z' /%3E%3C/svg%3E");
          }
        }
      }
    }

    &.vjs-ended:deep() {
      .vjs-poster {
        display: inline-block !important;
      }
    }
  }
</style>
